import {
  Configuration,
  PublicClientApplication,
  LogLevel,
  BrowserCacheLocation,
} from '@azure/msal-browser';

/**
 * MSAL configuration for the DevOps Reports SPA — Microsoft Entra, **multi-tenant** (B2B).
 * DOR's app registration is multi-tenant, so internal staff AND external/guest users sign in
 * with their own organisational account; the API validates a multi-tenant issuer. The SPA
 * acquires an access token for the DevOps Reports API.
 */
const clientId = (import.meta.env.VITE_AZURE_AD_CLIENT_ID as string | undefined) || '';

const msalConfig: Configuration = {
  auth: {
    clientId,
    // Multi-tenant, work/school accounts only: 'organizations' lets a user from ANY Microsoft
    // Entra tenant sign in with their org identity, but (unlike 'common') excludes personal
    // Microsoft accounts — matching DOR's app registration (AzureADMultipleOrgs, no MSA). Do
    // NOT pin this to a single tenant — that would block the external/guest tenants DOR serves.
    authority: 'https://login.microsoftonline.com/organizations',
    redirectUri: `${(import.meta.env.VITE_REDIRECT_URI as string | undefined) || window.location.origin}/auth/callback`,
    postLogoutRedirectUri: window.location.origin,
  },
  cache: {
    cacheLocation: BrowserCacheLocation.LocalStorage,
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return;
        if (level === LogLevel.Error) console.error('[MSAL]', message);
        else if (level === LogLevel.Warning) console.warn('[MSAL]', message);
      },
      logLevel: LogLevel.Warning,
      piiLoggingEnabled: false,
    },
  },
};

/** Scope for API access — the app exposes api://<clientId>/access_as_user.
 *  `prompt: 'select_account'` forces the account picker so a user with more than
 *  one cached account (e.g. a host-tenant admin + a guest) can choose which to
 *  sign in as, rather than MSAL silently reusing a stale cached account. */
export const loginRequest = {
  scopes: [`api://${clientId}/access_as_user`],
  prompt: 'select_account',
};

/** Scope used for silent token acquisition on API calls. */
export const tokenRequest = {
  scopes: [`api://${clientId}/access_as_user`],
};

/**
 * Loop guard for interactive re-auth (SPA auth standard, rule 4). Shared by the API client
 * and useAuth so a single sessionStorage flag governs both token-acquisition paths: fire
 * acquireTokenRedirect at most once per browser session, and clear it on the next
 * successful silent acquisition.
 */
export const REAUTH_GUARD_KEY = 'dor.reauth.attempted';

/** Shared MSAL instance — used by MsalProvider and by the API client for token acquisition. */
export const msalInstance = new PublicClientApplication(msalConfig);

/**
 * Initialise MSAL before rendering. Handles the redirect response on return from sign-in,
 * sets the active account, and validates any cached session. Mirrors CustomerSuccessPortal.
 */
export async function initializeMsal(): Promise<void> {
  await msalInstance.initialize();

  try {
    const response = await msalInstance.handleRedirectPromise();
    if (response?.account) {
      msalInstance.setActiveAccount(response.account);
      return;
    }
    // Only fall back to the first cached account when NO active account is set.
    // Unconditionally picking accounts[0] on every load clobbers the signed-in
    // account when multiple are cached (host-tenant admin + guest), silently
    // reverting to the wrong identity. MSAL persists the active account, so a
    // prior sign-in survives reloads.
    if (!msalInstance.getActiveAccount()) {
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        msalInstance.setActiveAccount(accounts[0]);
      }
    }
  } catch (error) {
    // Do NOT clear the cache here — wiping it discards the refresh token and forces a
    // fresh interactive sign-in on the next load. A failed redirect-response parse is
    // recoverable; the persisted session/refresh token should survive. clearMsalCache()
    // remains available for explicit "reset a broken auth state" recovery only.
    console.error('[MSAL] handleRedirectPromise error:', error);
  }
}

/** Clear MSAL localStorage entries to recover from a bad/looping auth state. */
export function clearMsalCache(): void {
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && (key.startsWith('msal.') || key.includes('login.windows.net') || key.includes('login.microsoftonline.com'))) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((k) => localStorage.removeItem(k));
}
